import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { Card, Button } from '../components';
import { documentApi } from '../services/api';
import { ContractVariationTag } from './ContractVariationTag';
import type { ContractFolderInfo } from '../utils/agreementIdentity';
import {
  attachmentErrorCode,
  ATTACHMENT_ACCEPT,
  ATTACHMENT_FORMATS_LABEL,
  MAX_ATTACHMENT_SIZE_MB,
} from '../utils/attachmentFiles';
import { typedPreviewBlob, parsePptxToHtml } from '../utils/documentPreview';
import { shortFileName } from '../utils/fileName';
import type { DocumentRecord, FolderTree, DmsFolder, DocumentSource } from '../types';

// A folder can be the project-level files, a contract number, or a custom folder.
type SelectedFolder = 'project' | number | { type: 'folder'; id: string };
type DocSortField = 'createdAt' | 'originalName' | 'size' | 'uploadedByName';
type SortDirection = 'asc' | 'desc';

const PREVIEWABLE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.docx', '.doc', '.xls', '.xlsx', '.ppt', '.pptx'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const WORD_EXTENSIONS = ['.doc', '.docx'];
const EXCEL_EXTENSIONS = ['.xls', '.xlsx'];
const PPTX_EXTENSIONS = ['.ppt', '.pptx'];

function getFileExtension(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.substring(idx).toLowerCase() : '';
}

function getFileIcon(name: string): string {
  const ext = getFileExtension(name);
  if (['.jpg', '.jpeg', '.png', '.heic'].includes(ext)) return 'feather-image';
  if (['.pdf', '.doc', '.docx', '.txt', '.xls', '.xlsx', '.csv', '.ppt', '.pptx'].includes(ext)) return 'feather-file-text';
  return 'feather-file';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export interface ProjectDocumentsTabProps {
  projectId: string;
  /** All documents for the project (already scoped to what the current role may see). */
  documents: DocumentRecord[];
  folderTree: FolderTree | null;
  canUpload: boolean;
  canRename: boolean;
  canDelete: boolean;
  isAdmin: boolean;
  /** Refetch documents + folder tree after a mutation. */
  onReload: () => void | Promise<void>;
  /** Optional file-info (integrity) modal opener supplied by the host view. */
  onShowFileInfo?: (doc: DocumentRecord) => void;
  /** Where an attachment's source opens for this viewer (ClickUp 869fj6n1d);
   * without it the source is shown as text. */
  sourceHref?: (source: DocumentSource) => string | undefined;
  /** Parent contract and ÄTA number per contract folder (ClickUp 869fmhd6p);
   * without it the contract folders are a flat list. */
  contractFolders?: ContractFolderInfo[];
}

/** The system folder "Bifogade filer": every attachment in the project. */
const isAttachmentsFolder = (folder: { systemKind?: string | null } | undefined) => folder?.systemKind === 'attachments';

/**
 * Shared documents tab used by both the company/admin project view and the
 * lawyer/inspector project view, so the two roles get the exact same visual
 * language (dark section headers, help texts, icons, empty state, card list on
 * mobile). Capability differences are expressed purely through the permission
 * props and the optional callbacks — no markup is duplicated per role.
 */
export function ProjectDocumentsTab({
  projectId,
  documents,
  folderTree,
  canUpload,
  canRename,
  canDelete,
  isAdmin,
  onReload,
  onShowFileInfo,
  sourceHref,
  contractFolders,
}: ProjectDocumentsTabProps) {
  const { t } = useTranslation();

  const [selectedFolder, setSelectedFolder] = useState<SelectedFolder>('project');
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [docSortField, setDocSortField] = useState<DocSortField>('createdAt');
  const [docSortDirection, setDocSortDirection] = useState<SortDirection>('desc');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [previewDoc, setPreviewDoc] = useState<DocumentRecord | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const [renameDoc, setRenameDoc] = useState<DocumentRecord | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState<DocumentRecord | null>(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolderTarget, setRenameFolderTarget] = useState<DmsFolder | null>(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<DmsFolder | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const reload = async () => { await onReload(); };

  const filteredDocuments = useMemo(() => {
    const result = documents.filter(d => {
      if (selectedFolder === 'project') return d.contractNumber === null && !d.folderId;
      // A contract's folder also shows attachments from messages about the
      // contract or its stages: the same file as in "Bifogade filer", not a
      // contract attachment (ClickUp 869fj6n1d).
      if (typeof selectedFolder === 'number') return d.contractNumber === selectedFolder || d.source?.contractNumber === selectedFolder;
      if (typeof selectedFolder === 'object' && selectedFolder.type === 'folder') return d.folderId === selectedFolder.id;
      return true;
    });

    result.sort((a, b) => {
      if (docSortField === 'size') {
        return docSortDirection === 'asc' ? a.size - b.size : b.size - a.size;
      }
      const aVal = String((a as unknown as Record<string, unknown>)[docSortField] || '').toLowerCase();
      const bVal = String((b as unknown as Record<string, unknown>)[docSortField] || '').toLowerCase();
      if (aVal < bVal) return docSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return docSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [documents, selectedFolder, docSortField, docSortDirection]);

  const folderLabel = (folder: DmsFolder) => isAttachmentsFolder(folder) ? t('documents.systemFolders.attachments') : folder.name;
  const selectedCustomFolder = typeof selectedFolder === 'object' && selectedFolder.type === 'folder'
    ? folderTree?.folders?.find(f => f.id === selectedFolder.id)
    : undefined;
  // Each contract followed by its ÄTA in ÄTA order; an ÄTA whose contract has
  // no folder stays at the top level with its tag (ClickUp 869fmhd6p).
  const contractFolderRows = useMemo(() => {
    const folders = [...(folderTree?.contracts ?? [])].sort((a, b) => a.contractNumber - b.contractNumber);
    const info = new Map((contractFolders ?? []).map(item => [item.contractNumber, item]));
    const numbers = new Set(folders.map(f => f.contractNumber));
    const parentOf = (n: number) => {
      const parent = info.get(n)?.parentContractNumber;
      return parent != null && parent !== n && numbers.has(parent) ? parent : null;
    };
    const variationOf = (n: number) => info.get(n)?.variationNumber ?? null;
    return folders.filter(f => parentOf(f.contractNumber) === null).flatMap(folder => [
      { folder, child: false, variationNumber: variationOf(folder.contractNumber) },
      ...folders
        .filter(f => parentOf(f.contractNumber) === folder.contractNumber)
        .sort((a, b) => (variationOf(a.contractNumber) ?? 0) - (variationOf(b.contractNumber) ?? 0))
        .map(f => ({ folder: f, child: true, variationNumber: variationOf(f.contractNumber) })),
    ]);
  }, [folderTree, contractFolders]);
  const contractFolderName = (contractNumber: number) => {
    const row = contractFolderRows.find(r => r.folder.contractNumber === contractNumber);
    const name = row?.variationNumber != null
      ? t('contracts.list.variationTag', { number: row.variationNumber })
      : t('contracts.list.contractName', { number: contractNumber });
    return row?.folder.contractName ? `${name} · ${row.folder.contractName}` : name;
  };
  const currentFolderName =
    selectedFolder === 'project'
      ? t('documents.folderTree.projectFiles')
      : typeof selectedFolder === 'number'
        ? contractFolderName(selectedFolder)
        : selectedCustomFolder ? folderLabel(selectedCustomFolder) : '';
  // Attachments come with messages; nothing is uploaded straight into the
  // system folder (ClickUp 869fj6n1d).
  const canUploadHere = canUpload && !isAttachmentsFolder(selectedCustomFolder);
  // The system folder first, then the user's own folders.
  const orderedFolders = [...(folderTree?.folders ?? [])].sort((a, b) => Number(isAttachmentsFolder(b)) - Number(isAttachmentsFolder(a)));
  const attachmentsFolderId = orderedFolders.find(isAttachmentsFolder)?.id;
  /** An attachment stays as evidence: it cannot be renamed, and only an admin may delete it. */
  const isLockedAttachment = (doc: DocumentRecord) => !!doc.source || (!!attachmentsFolderId && doc.folderId === attachmentsFolderId);
  const sourceLabel = (source: DocumentSource) => {
    const objection = ['objection', 'objection_reply', 'conflict', 'remedy'].includes(source.context ?? '');
    const where = source.threadType === 'milestone'
      ? t('documents.source.stage', { number: source.milestoneNumber ?? '', description: source.milestoneDescription ?? '' })
      : source.threadType === 'contract'
        ? t('documents.source.contract', { number: source.contractNumber ?? '' })
        : t('documents.source.project');
    return `${t(objection ? 'documents.source.objection' : 'documents.source.message')} · ${where}`;
  };
  const renderOrigin = (doc: DocumentRecord) => {
    if (!doc.source) return null;
    const href = sourceHref?.(doc.source);
    const label = sourceLabel(doc.source);
    return (
      <span className="doc-source">
        <i className={`feather-${doc.source.context && doc.source.context !== 'attestation_question' ? 'alert-triangle' : 'message-square'}`} aria-hidden="true"></i>
        {href ? <Link to={href}>{label}</Link> : label}
      </span>
    );
  };

  const handleFileUpload = async (files: FileList | File[]) => {
    if (!projectId) return;
    const fileArr = Array.from(files);
    const emptyFiles = fileArr.filter(f => f.size === 0);
    const nonEmpty = fileArr.filter(f => f.size > 0);
    if (emptyFiles.length > 0 && nonEmpty.length === 0) {
      setMessage({ type: 'error', text: `${t('documents.upload.errorEmpty')} (${emptyFiles.map(f => f.name).join(', ')})` });
      return;
    }
    setIsUploading(true);
    setMessage(null);
    try {
      const contractNum = typeof selectedFolder === 'number' ? selectedFolder : null;
      const folderId = typeof selectedFolder === 'object' && selectedFolder.type === 'folder' ? selectedFolder.id : null;
      await documentApi.uploadFiles(projectId, nonEmpty, contractNum, folderId);
      if (emptyFiles.length > 0) {
        setMessage({ type: 'error', text: `${t('documents.upload.errorEmpty')} (${emptyFiles.map(f => f.name).join(', ')})` });
      } else {
        setMessage({ type: 'success', text: t('documents.upload.success') });
      }
      await reload();
    } catch (error) {
      const key = attachmentErrorCode(error) === 'FILE_EMPTY' ? 'documents.upload.errorEmpty' : 'documents.upload.error';
      setMessage({ type: 'error', text: t(key) });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files);
      e.target.value = '';
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current++;
    if (dragCounter.current === 1) setIsDragging(true);
  };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) handleFileUpload(e.dataTransfer.files);
  };

  const handleDocSelect = (docId: string) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };
  const handleSelectAll = () => {
    if (selectedDocIds.size === filteredDocuments.length) setSelectedDocIds(new Set());
    else setSelectedDocIds(new Set(filteredDocuments.map(d => d.id)));
  };
  const handleDownloadSelected = async () => {
    if (selectedDocIds.size === 0) return;
    if (selectedDocIds.size === 1) {
      const doc = documents.find(d => d.id === Array.from(selectedDocIds)[0]);
      if (doc) await documentApi.downloadFile(doc.id, doc.originalName);
    } else {
      await documentApi.downloadZip(Array.from(selectedDocIds));
    }
  };

  const handlePreview = async (doc: DocumentRecord) => {
    try {
      const blob = await documentApi.getFileBlob(doc.id);
      const ext = getFileExtension(doc.originalName);
      if (WORD_EXTENSIONS.includes(ext)) {
        const arrayBuffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setPreviewDoc(doc); setPreviewHtml(result.value); setPreviewUrl(null);
      } else if (EXCEL_EXTENSIONS.includes(ext)) {
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        let html = '';
        for (const name of workbook.SheetNames) {
          if (workbook.SheetNames.length > 1) html += `<h5 style="margin-top:1rem">${name}</h5>`;
          html += XLSX.utils.sheet_to_html(workbook.Sheets[name], { editable: false });
        }
        setPreviewDoc(doc); setPreviewHtml(html); setPreviewUrl(null);
      } else if (PPTX_EXTENSIONS.includes(ext)) {
        const arrayBuffer = await blob.arrayBuffer();
        const html = await parsePptxToHtml(arrayBuffer);
        setPreviewDoc(doc); setPreviewHtml(html); setPreviewUrl(null);
      } else {
        // PDFs/images: give the blob a real MIME type so it renders instead of
        // showing as raw text.
        const url = URL.createObjectURL(typedPreviewBlob(blob, doc.originalName));
        setPreviewDoc(doc); setPreviewUrl(url); setPreviewHtml(null);
      }
    } catch (error) {
      console.error('Preview error:', error);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewHtml(null);
  };

  const handleRenameSubmit = async () => {
    if (!renameDoc || !renameValue.trim()) return;
    setMessage(null);
    try {
      await documentApi.renameDocument(renameDoc.id, renameValue.trim());
      setMessage({ type: 'success', text: t('documents.rename.success') });
      setRenameDoc(null);
      setRenameValue('');
      await reload();
    } catch {
      setMessage({ type: 'error', text: t('documents.rename.error') });
    }
  };

  const confirmDeleteDocument = async () => {
    if (!deleteConfirmDoc) return;
    const target = deleteConfirmDoc;
    setDeleteConfirmDoc(null);
    setMessage(null);
    try {
      await documentApi.deleteDocument(target.id);
      setMessage({ type: 'success', text: t('documents.delete.success') });
      setSelectedDocIds(prev => { const next = new Set(prev); next.delete(target.id); return next; });
      await reload();
    } catch {
      setMessage({ type: 'error', text: t('documents.delete.error') });
    }
  };

  const handleDocSort = (field: DocSortField) => {
    if (docSortField === field) {
      setDocSortDirection(docSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setDocSortField(field);
      setDocSortDirection(field === 'createdAt' ? 'desc' : 'asc');
    }
  };

  const handleCreateFolder = async () => {
    if (!projectId || !newFolderName.trim()) return;
    setMessage(null);
    try {
      await documentApi.createFolder(projectId, newFolderName.trim());
      setMessage({ type: 'success', text: t('documents.folders.createSuccess') });
      setShowCreateFolder(false);
      setNewFolderName('');
      await reload();
    } catch {
      setMessage({ type: 'error', text: t('documents.folders.createError') });
    }
  };

  const handleRenameFolderSubmit = async () => {
    if (!renameFolderTarget || !renameFolderValue.trim()) return;
    setMessage(null);
    try {
      await documentApi.renameFolder(renameFolderTarget.id, renameFolderValue.trim());
      setMessage({ type: 'success', text: t('documents.folders.renameSuccess') });
      setRenameFolderTarget(null);
      setRenameFolderValue('');
      await reload();
    } catch {
      setMessage({ type: 'error', text: t('documents.folders.renameError') });
    }
  };

  const handleDeleteFolderConfirm = async () => {
    if (!deleteFolderTarget) return;
    const target = deleteFolderTarget;
    setMessage(null);
    try {
      await documentApi.deleteFolder(target.id);
      setMessage({ type: 'success', text: t('documents.folders.deleteSuccess') });
      if (typeof selectedFolder === 'object' && selectedFolder.type === 'folder' && selectedFolder.id === target.id) {
        setSelectedFolder('project');
      }
      setDeleteFolderTarget(null);
      await reload();
    } catch {
      setMessage({ type: 'error', text: t('documents.folders.deleteError') });
    }
  };

  const allSelected = selectedDocIds.size === filteredDocuments.length && filteredDocuments.length > 0;

  const renderRowActions = (doc: DocumentRecord, compact: boolean) => (
    <div className={`doc-row-actions ${compact ? 'd-flex gap-1' : 'd-inline-flex gap-1'}`}>
      {onShowFileInfo && (
        <button className="btn btn-sm btn-light" title={t('documents.actions.fileInfo')} onClick={() => onShowFileInfo(doc)}>
          <i className="feather-info me-1"></i>
          {!compact && <span className="d-none d-xl-inline">{t('documents.actions.fileInfo')}</span>}
        </button>
      )}
      <button
        className="btn btn-sm btn-primary"
        title={t('documents.actions.download')}
        onClick={() => documentApi.downloadFile(doc.id, doc.originalName)}
      >
        <i className="feather-download me-1"></i>
        {!compact && <span className="d-none d-xl-inline">{t('documents.actions.download')}</span>}
      </button>
      {canRename && !isLockedAttachment(doc) && (
        <button
          className="btn btn-sm btn-light"
          title={t('documents.actions.rename')}
          onClick={() => { setRenameDoc(doc); setRenameValue(doc.originalName); }}
        >
          <i className="feather-edit-2 me-1"></i>
          {!compact && <span className="d-none d-xl-inline">{t('documents.actions.rename')}</span>}
        </button>
      )}
      {PREVIEWABLE_EXTENSIONS.includes(getFileExtension(doc.originalName)) && (
        <button className="btn btn-sm btn-light" title={t('documents.actions.preview')} onClick={() => handlePreview(doc)}>
          <i className={compact ? 'feather-eye' : 'feather-external-link'}></i>
        </button>
      )}
      {canDelete && (
        <button className="btn btn-sm btn-light text-danger" title={t('documents.actions.delete')} onClick={() => setDeleteConfirmDoc(doc)}>
          <i className="feather-trash-2"></i>
        </button>
      )}
    </div>
  );

  return (
    <div className="tab-pane fade show active">
      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Topbar: bulk actions (upload lives in the folder panel, sorting in the file panel) */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        {canUpload && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
            accept={ATTACHMENT_ACCEPT}
          />
        )}
        {selectedDocIds.size > 0 && (
          <Button variant="light-brand" onClick={handleDownloadSelected}>
            <i className="feather-download me-2"></i>
            {t('documents.actions.downloadSelected')} ({selectedDocIds.size})
          </Button>
        )}
      </div>

      {canUpload && (
        <p className="text-muted small mb-3">
          <i className="feather-info me-1"></i>
          {t('documents.upload.formatsHint', { formats: ATTACHMENT_FORMATS_LABEL, max: MAX_ATTACHMENT_SIZE_MB })}
        </p>
      )}

      <div className="row">
        {/* Folder Tree */}
        <div className="col-md-3">
          <Card>
            <div className="card-header d-flex align-items-center">
              <h5 className="card-title mb-0">
                <i className="feather-folder me-2"></i>
                {t('documents.folders.title')}
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                <button
                  className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedFolder === 'project' ? 'active' : ''}`}
                  onClick={() => { setSelectedFolder('project'); setSelectedDocIds(new Set()); }}
                >
                  <span><i className="feather-folder me-2"></i>{t('documents.folderTree.projectFiles')}</span>
                  <span className="badge bg-secondary rounded-pill">{folderTree?.projectFileCount ?? 0}</span>
                </button>
                {contractFolderRows.map(({ folder: c, child, variationNumber }) => (
                  <button
                    key={c.contractNumber}
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center doc-contract-folder ${child ? 'contract-list-child-cell' : ''} ${selectedFolder === c.contractNumber ? 'active' : ''}`}
                    onClick={() => { setSelectedFolder(c.contractNumber); setSelectedDocIds(new Set()); }}
                    title={contractFolderName(c.contractNumber)}
                  >
                    <span className="d-flex align-items-center doc-contract-folder-name">
                      {variationNumber == null && <i className="feather-folder me-2"></i>}
                      {variationNumber != null && <ContractVariationTag number={variationNumber} />}
                      <span className="doc-contract-folder-text">
                        {variationNumber != null
                          ? c.contractName
                          : c.contractName
                            ? `${t('contracts.list.contractName', { number: c.contractNumber })} · ${c.contractName}`
                            : t('contracts.list.contractName', { number: c.contractNumber })}
                      </span>
                    </span>
                    <span className="badge bg-secondary rounded-pill">{c.fileCount}</span>
                  </button>
                ))}
                {orderedFolders.map(f => (
                  <button
                    key={f.id}
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${typeof selectedFolder === 'object' && selectedFolder.type === 'folder' && selectedFolder.id === f.id ? 'active' : ''}`}
                    onClick={() => { setSelectedFolder({ type: 'folder', id: f.id }); setSelectedDocIds(new Set()); }}
                  >
                    {/* The name wraps rather than being cut away in the narrow column. */}
                    <span className="d-flex align-items-center doc-folder-name">
                      <i className={`${isAttachmentsFolder(f) ? 'feather-paperclip' : 'feather-folder-plus'} me-2`}></i>
                      <span>{folderLabel(f)}</span>
                    </span>
                    <span className="d-flex align-items-center gap-1">
                      {isAttachmentsFolder(f) && (
                        <i className="feather-lock doc-folder-lock" title={t('documents.systemFolders.locked')} aria-label={t('documents.systemFolders.locked')}></i>
                      )}
                      <span className="badge bg-secondary rounded-pill">{f.fileCount}</span>
                      {isAdmin && !isAttachmentsFolder(f) && (
                        <span className="d-flex gap-0">
                          <span role="button" className="px-1" title={t('documents.folders.rename')}
                            onClick={(e) => { e.stopPropagation(); setRenameFolderTarget(f); setRenameFolderValue(f.name); }}>
                            <i className="feather-edit-2" style={{ fontSize: '0.75rem' }}></i>
                          </span>
                          <span role="button" className="px-1 text-danger" title={t('documents.folders.delete')}
                            onClick={(e) => { e.stopPropagation(); setDeleteFolderTarget(f); }}>
                            <i className="feather-trash-2" style={{ fontSize: '0.75rem' }}></i>
                          </span>
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
              {isAdmin && (
                <div className="p-2 border-top">
                  <button className="btn btn-sm btn-light-primary w-100" onClick={() => { setShowCreateFolder(true); setNewFolderName(''); }}>
                    <i className="feather-plus me-1"></i>
                    {t('documents.folders.create')}
                  </button>
                </div>
              )}
            </div>
            {canUpload && (
              <div className="card-footer">
                <Button variant="success" className="w-100" onClick={() => fileInputRef.current?.click()} disabled={isUploading || !canUploadHere}>
                  <i className="feather-upload me-2"></i>
                  {isUploading ? t('documents.upload.uploading') : t('documents.upload.button')}
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* File List */}
        <div className="col-md-9">
          <Card>
            <div className="card-header d-flex align-items-center justify-content-between">
              <h5 className="card-title mb-0 text-truncate">
                <i className="feather-file-text me-2"></i>
                {t('documents.list.filesIn', { folder: currentFolderName })}
              </h5>
              <span className="small flex-shrink-0 ms-2">
                {t('documents.list.fileCount', { count: filteredDocuments.length })}
              </span>
            </div>
            <div
              className={`card-body${isDragging ? ' bg-light border border-primary border-dashed' : ''}`}
              onDragEnter={canUploadHere ? handleDragEnter : undefined}
              onDragOver={canUploadHere ? handleDragOver : undefined}
              onDragLeave={canUploadHere ? handleDragLeave : undefined}
              onDrop={canUploadHere ? handleDrop : undefined}
              style={{ minHeight: '300px' }}
            >
              {!isDragging && filteredDocuments.length > 0 && (
                <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
                  <small className="text-muted">{t('documents.filter.sortBy')}:</small>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={docSortField}
                    onChange={e => setDocSortField(e.target.value as DocSortField)}
                  >
                    <option value="createdAt">{t('documents.filter.sortDate')}</option>
                    <option value="originalName">{t('documents.filter.sortName')}</option>
                    <option value="size">{t('documents.filter.sortSize')}</option>
                    <option value="uploadedByName">{t('documents.filter.sortUploader')}</option>
                  </select>
                  <button className="btn btn-sm btn-light" onClick={() => setDocSortDirection(docSortDirection === 'asc' ? 'desc' : 'asc')}>
                    <i className={`feather-chevron-${docSortDirection === 'asc' ? 'up' : 'down'}`}></i>
                  </button>
                </div>
              )}

              {isDragging && (
                <div className="text-center py-5">
                  <i className="feather-upload fs-1 text-primary mb-3 d-block"></i>
                  <h5 className="text-primary">{t('documents.upload.dragDrop')}</h5>
                </div>
              )}

              {!isDragging && filteredDocuments.length === 0 && (
                <div className="text-center py-5">
                  <i className="feather-folder fs-1 text-muted mb-3 d-block"></i>
                  <h5>{t('documents.list.noFiles')}</h5>
                  <p className="text-muted">{t('documents.list.noFilesDescription')}</p>
                </div>
              )}

              {!isDragging && filteredDocuments.length > 0 && (
                <>
                  {/* Desktop table */}
                  <div className="table-responsive d-none d-md-block">
                    <table className="table table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input type="checkbox" className="form-check-input" checked={allSelected} onChange={handleSelectAll} />
                          </th>
                          <th onClick={() => handleDocSort('originalName')} style={{ cursor: 'pointer' }}>
                            {t('documents.list.name')}
                            {docSortField === 'originalName' && <i className={`feather-chevron-${docSortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                          </th>
                          <th onClick={() => handleDocSort('createdAt')} style={{ cursor: 'pointer' }}>
                            {t('documents.list.date')}
                            {docSortField === 'createdAt' && <i className={`feather-chevron-${docSortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                          </th>
                          <th onClick={() => handleDocSort('size')} style={{ cursor: 'pointer' }}>
                            {t('documents.list.size')}
                            {docSortField === 'size' && <i className={`feather-chevron-${docSortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                          </th>
                          <th className="text-end">{t('documents.list.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDocuments.map(doc => (
                          <tr key={doc.id}>
                            <td>
                              <input type="checkbox" className="form-check-input" checked={selectedDocIds.has(doc.id)} onChange={() => handleDocSelect(doc.id)} />
                            </td>
                            <td className="text-truncate" style={{ maxWidth: '320px' }} title={doc.originalName}>
                              <i className={`${getFileIcon(doc.originalName)} me-2`}></i>
                              {shortFileName(doc.originalName)}
                              {doc.source && (
                                <span className="doc-origin">
                                  {doc.uploadedByName && <span>{doc.uploadedByName}</span>}
                                  {renderOrigin(doc)}
                                </span>
                              )}
                            </td>
                            <td className="text-nowrap">{new Date(doc.createdAt).toLocaleDateString('sv-SE')}</td>
                            <td className="text-nowrap">{formatFileSize(doc.size)}</td>
                            <td className="text-end">{renderRowActions(doc, false)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile card list */}
                  <div className="d-md-none">
                    <div className="d-flex align-items-center mb-2 px-1">
                      <input type="checkbox" className="form-check-input me-2" checked={allSelected} onChange={handleSelectAll} />
                      <small className="text-muted">{t('documents.actions.selectAll')}</small>
                    </div>
                    {filteredDocuments.map(doc => (
                      <div key={doc.id} className={`border rounded p-3 mb-2 ${selectedDocIds.has(doc.id) ? 'border-primary bg-light' : ''}`}>
                        <div className="d-flex align-items-start gap-2">
                          <input type="checkbox" className="form-check-input mt-1 flex-shrink-0" checked={selectedDocIds.has(doc.id)} onChange={() => handleDocSelect(doc.id)} />
                          <div className="flex-grow-1 min-width-0">
                            <div className="fw-medium text-break">
                              <i className={`${getFileIcon(doc.originalName)} me-1`}></i>
                              {doc.originalName}
                            </div>
                            <div className="d-flex flex-wrap gap-2 mt-1">
                              <small className="text-muted">{formatFileSize(doc.size)}</small>
                              <small className="text-muted">{doc.uploadedByName}</small>
                              <small className="text-muted">{new Date(doc.createdAt).toLocaleDateString('sv-SE')}</small>
                            </div>
                            {doc.source && <div className="doc-origin mt-1">{renderOrigin(doc)}</div>}
                          </div>
                        </div>
                        <div className="mt-2 ms-4">{renderRowActions(doc, true)}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Rename Modal */}
      {renameDoc && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('documents.rename.title')}</h5>
                <button type="button" className="btn-close" onClick={() => setRenameDoc(null)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label">{t('documents.rename.label')}</label>
                <input type="text" className="form-control" value={renameValue} onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleRenameSubmit(); }} autoFocus />
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={() => setRenameDoc(null)}>{t('common.cancel')}</Button>
                <Button onClick={handleRenameSubmit}>{t('common.save')}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmDoc && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('documents.actions.delete')}</h5>
                <button type="button" className="btn-close" onClick={() => setDeleteConfirmDoc(null)}></button>
              </div>
              <div className="modal-body">
                <p>{t('documents.delete.confirm')}</p>
                <p className="text-muted mb-0">
                  <i className={`${getFileIcon(deleteConfirmDoc.originalName)} me-2`}></i>
                  {deleteConfirmDoc.originalName}
                </p>
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={() => setDeleteConfirmDoc(null)}>{t('common.cancel')}</Button>
                <Button variant="danger" onClick={confirmDeleteDocument}>
                  <i className="feather-trash-2 me-2"></i>{t('documents.actions.delete')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('documents.folders.create')}</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateFolder(false)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label">{t('documents.folders.nameLabel')}</label>
                <input type="text" className="form-control" placeholder={t('documents.folders.namePlaceholder')} value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); }} autoFocus />
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={() => setShowCreateFolder(false)}>{t('common.cancel')}</Button>
                <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>{t('documents.folders.create')}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {renameFolderTarget && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('documents.folders.rename')}</h5>
                <button type="button" className="btn-close" onClick={() => setRenameFolderTarget(null)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label">{t('documents.folders.nameLabel')}</label>
                <input type="text" className="form-control" value={renameFolderValue} onChange={e => setRenameFolderValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleRenameFolderSubmit(); }} autoFocus />
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={() => setRenameFolderTarget(null)}>{t('common.cancel')}</Button>
                <Button onClick={handleRenameFolderSubmit} disabled={!renameFolderValue.trim()}>{t('common.save')}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Modal */}
      {deleteFolderTarget && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('documents.folders.delete')}</h5>
                <button type="button" className="btn-close" onClick={() => setDeleteFolderTarget(null)}></button>
              </div>
              <div className="modal-body">
                <p>{t('documents.folders.confirmDelete')}</p>
                <p className="text-muted mb-0"><i className="feather-folder-plus me-2"></i>{deleteFolderTarget.name}</p>
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={() => setDeleteFolderTarget(null)}>{t('common.cancel')}</Button>
                <Button variant="danger" onClick={handleDeleteFolderConfirm}>
                  <i className="feather-trash-2 me-2"></i>{t('documents.folders.delete')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewDoc && (previewUrl || previewHtml) && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('documents.preview.title')} - {previewDoc.originalName}</h5>
                <button type="button" className="btn-close" onClick={closePreview}></button>
              </div>
              <div className="modal-body" style={{ minHeight: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
                {previewHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} style={{ textAlign: 'left' }} />
                ) : IMAGE_EXTENSIONS.includes(getFileExtension(previewDoc.originalName)) ? (
                  <div className="text-center">
                    <img src={previewUrl!} alt={previewDoc.originalName} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
                  </div>
                ) : getFileExtension(previewDoc.originalName) === '.pdf' ? (
                  <iframe src={previewUrl!} style={{ width: '100%', height: '70vh', border: 'none' }} title={previewDoc.originalName} />
                ) : (
                  <p className="text-muted text-center">{t('documents.preview.notSupported')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
